import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import classnames from 'classnames';
import React, { Component, PropsWithChildren, KeyboardEvent, SyntheticEvent } from 'react';
import { Icon } from '../Icon';
import { ICardProps } from './types';

import { BackgroundImage } from '../BackgroundImage';
import '../styles/colors.scss';
import './Card.scss';

const spacebarkey = 32;

/**
 * The `<Card>` component is based off the bootstrap v4 Card component.
 * https://getbootstrap.com/docs/4.0/components/card/
 * It is configurable with several optional props including text, image, link, etc.
 * It can also be used as a 'selectable' component, by including an 'onSelect' callback as a prop.
 * Initial 'isSelected' state is false unless given isSelected prop.
 * Card is also keyboard accessible; tab key will focus on card, and space key will select
 *
 * Images will scale while attempting to keep ratio, so if you don't set a width, you will end up with large cards.
 */
export class Card extends Component<PropsWithChildren<ICardProps>, {}> {
  public render() {
    const {
      button,
      backgroundImage,
      image,
      link,
      selected = false,
      multiSelect = false,
      onSelect,
      subtitle,
      textAlignment = 'left',
      text,
      title,
      width,
      height = 410,
      backgroundColor = '#efefef',
    } = this.props;
    const tabIndex = !!onSelect ? 0 : -1;
    const cardClasses = classnames({
      card: true,
      'card-bg-light': !selected,
      'card-bg-selected': selected,
      'selectable-card': !!onSelect,
      'text-center': textAlignment === 'center',
      'text-left': textAlignment === 'left',
      'text-right': textAlignment === 'right',
    });
    const subtitleClasses = classnames({
      'card-subtitle': true,
      'text-muted': !selected,
      'text-white': selected,
    });
    const linkClasses = classnames({
      'card-link': true,
      'text-white': selected,
    });

    return (
      <div
        style={{ height, width }}
        className={cardClasses}
        onClick={this.onSelect}
        tabIndex={tabIndex}
        onKeyDown={this.handleKeyDown}
      >
        {selected && (
          <div className={`selected-check ${selected ? 'on' : 'off'}`}>
            <Icon icon={faCheckCircle} size="lg" />
          </div>
        )}

        {image && (
          <div style={{ backgroundColor }} className="card-img-top">
            {' '}
            {image}
          </div>
        )}
        {backgroundImage && (
          <div style={{ backgroundColor }} className="card-img-top">
            <BackgroundImage height={210} {...backgroundImage} />
          </div>
        )}
        <div className="card-body">
          {title && <h5 className="card-title">{title}</h5>}
          {subtitle && <h6 className={subtitleClasses}>{subtitle}</h6>}
          {text && <p className="card-text">{text}</p>}
          {link && <span className={linkClasses}>{link}</span>}
          {button}
        </div>
      </div>
    );
  }

  private onSelect = (e: SyntheticEvent<EventTarget>) => {
    const { id, selected, onSelect } = this.props;
    const isLinkClick = e.target instanceof HTMLAnchorElement;
    const isButtonClick = e.target instanceof HTMLButtonElement;
    const isSelectableCard = !!onSelect;

    if (!isLinkClick && !isButtonClick && isSelectableCard) {
      e.preventDefault();
      onSelect && onSelect(id, !selected);
    }
  };

  private handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.keyCode === spacebarkey) {
      this.onSelect(event);
    }
  };
}

export default Card;
